import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { Booking, BookingStatus, DriverDoc } from "../../types/company";
import { FINAL_STATUSES, OPEN_STATUSES } from "../../data/company";
import {
  availableCount,
  daysBetween,
  driverName,
  findBooking,
  findDriver,
  findListing,
  findUnit,
  initials,
  listingName,
  pickupAddress,
  unitConflicts,
  unitFree,
  unitsOf,
} from "../../lib/company";
import { useCompany } from "../../lib/companyContext";
import { useBookingApi } from "../../components/company/useBookingApi";
import { cn, eur, longDate, shortDate, shortDateTime } from "../../lib/format";
import { dailyRate } from "../../lib/rentalPrice";
import {
  ActionButton,
  BackLink,
  Card,
  CardTitle,
  ChoiceRow,
  DetailRow,
  Hint,
  MessageBubble,
  MessageInput,
  Modal,
  StatusBadge,
} from "../../components/company/ui";
import { useT } from "../../i18n";

const DOT: Record<BookingStatus, string> = {
  pending: "bg-[#E8B04B]",
  documents_required: "bg-[#E8B04B]",
  confirmed: "bg-brand",
  active: "bg-tone-green-fg",
  completed: "bg-ink",
  declined: "bg-tone-red-fg",
  cancelled: "bg-faint",
  expired: "bg-faint",
};

const textArea =
  "w-full resize-y rounded border bg-surface px-3.5 py-3 text-sm outline-none";

type ModalState =
  | { kind: "approve" | "assign" }
  | { kind: "info" | "reject" }
  | { kind: "doc"; doc: DriverDoc }
  | null;

export default function CompanyBookingDetail() {
  const t = useT();
  const bd = t.company.booking;
  const { id = "" } = useParams();
  const { state, toast } = useCompany();
  const api = useBookingApi();
  const [modal, setModal] = useState<ModalState>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState("");

  // Loads the latest version and marks the driver's messages as read
  useEffect(() => {
    void api.open(id).then(() => setOpened(id));
  }, [api, id]);

  const booking = findBooking(state, id);
  if (!booking) {
    return <p className="text-sm text-muted">{state.bookingsLoaded && opened === id ? bd.notFound : t.company.listings.loading}</p>;
  }

  const driver = findDriver(state, booking.driverId);
  const listing = findListing(state, booking.listingId);
  // The driver comes with the booking; the listing may still be loading (or deleted)
  if (!driver) return <p className="text-sm text-muted">{t.company.listings.loading}</p>;
  const unit = booking.unitId ? findUnit(state, booking.unitId) : undefined;
  const nights = daysBetween(booking.start, booking.end);
  const { terms } = booking;
  const contactShared = state.bookings.some(
    (b) => b.driverId === driver.id && ["confirmed", "active", "completed"].includes(b.status),
  );
  const canDecide = OPEN_STATUSES.includes(booking.status);
  const isFinal = FINAL_STATUSES.includes(booking.status);

  const act = async (action: "start" | "complete", success: string) => {
    setBusy(true);
    const error = await api.act(booking.id, action);
    setBusy(false);
    if (error) toast.error(error);
    else toast.success(success);
  };

  const meta = (from: "company" | "driver" | "system", at: string) =>
    `${from === "company" ? bd.you : from === "driver" ? driverName(driver) : bd.platform} · ${shortDateTime(at)}`;

  return (
    <>
      <BackLink to="/company/bookings">{t.company.bookings.title}</BackLink>
      <div className="mt-2.5 mb-4.5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="m-0 text-[26px] font-medium">
            {bd.title} <span className="font-mono">{booking.id}</span>
          </h1>
          <div className="mt-1 text-sm text-muted">{bd.received(shortDateTime(booking.createdAt))}</div>
        </div>
        <StatusBadge kind="booking" status={booking.status} className="px-2.5 py-1.5 text-[13px]" />
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="grid min-w-0 gap-4">
          <Card>
            <CardTitle className="mb-2.5">{bd.requestCard}</CardTitle>
            <DetailRow label={bd.vehicle} value={listing ? `${listingName(listing)} · ${listing.city}` : listingName(listing)} />
            {listing && pickupAddress(listing) && <DetailRow label={bd.pickup} value={pickupAddress(listing)} />}
            <DetailRow label={bd.period} value={bd.periodValue(longDate(booking.start), longDate(booking.end), nights)} />
            <DetailRow
              label={bd.rate}
              value={`${eur(dailyRate(terms))}${t.common.perDay} · ${eur(terms.weekly)}${t.common.perWeekShort} · ${eur(terms.monthly)}${t.common.perMonth}`}
            />
            {terms.estimate != null && <DetailRow label={bd.estimate} value={eur(terms.estimate)} />}
            <DetailRow label={bd.deposit} value={eur(terms.deposit)} />
            <DetailRow label={bd.kmIncluded} value={bd.kmValue(terms.kmIncluded)} />
            <DetailRow
              label={bd.availableForPeriod}
              value={listing ? `${availableCount(state, listing.id, booking.start, booking.end)} / ${unitsOf(state, listing.id).length}` : "—"}
            />
            {booking.note && (
              <div className="mt-3.5 rounded-md bg-surface px-3.5 py-3 text-sm leading-normal">
                « {booking.note} »<div className="mt-1 text-xs text-muted">{bd.driverMessage}</div>
              </div>
            )}
          </Card>

          <Card>
            <div className="mb-3.5 flex items-center gap-3.5">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-ink font-medium text-white">
                {initials(driver)}
              </div>
              <div className="flex-1">
                <div className="text-base font-medium">{driverName(driver)}</div>
                <div className="text-[13px] text-muted">
                  {bd.driverMeta(driver.city, longDate(driver.createdAt), driver.licenceYear)}
                </div>
              </div>
              <StatusBadge kind="driver" status={driver.status} />
            </div>
            <div className="mb-3 text-[13px] text-ink-soft">
              {contactShared
                ? `${driver.email} · ${driver.phone}`
                : bd.contactHidden}
            </div>
            <h3 className="m-0 mb-1.5 text-sm font-medium">{bd.driverDocs}</h3>
            {driver.docs.map((doc) => (
              <div key={doc.type} className="flex flex-wrap items-center gap-2.5 border-t border-surface py-2.5 text-sm">
                <span className="min-w-37.5 flex-1">
                  {t.company.docTypes[doc.type]}
                  <span className="block text-xs text-faint">
                    {doc.expiry
                      ? bd.validUntil(longDate(doc.expiry))
                      : doc.status === "missing"
                        ? bd.notProvided
                        : ""}
                  </span>
                </span>
                <StatusBadge kind="driverDoc" status={doc.status} />
                {doc.file && (
                  <ActionButton size="sm" className="font-normal" onClick={() => setModal({ kind: "doc", doc })}>
                    {t.common.view}
                  </ActionButton>
                )}
              </div>
            ))}
            <Hint className="mt-2.5">
              {bd.docsHint}
            </Hint>
          </Card>

          <Card>
            <CardTitle className="mb-3">{bd.messages}</CardTitle>
            <div className="grid gap-2">
              {booking.messages.map((m, i) => (
                <MessageBubble key={i} from={m.from} text={m.text} meta={meta(m.from, m.at)} />
              ))}
            </div>
            {booking.messages.length === 0 && <p className="m-0 text-sm text-faint">{bd.noMessages}</p>}
            <div className="mt-3">
              <MessageInput
                value={message}
                onChange={setMessage}
                placeholder={bd.writeToDriver}
                onSend={async () => {
                  const text = message.trim();
                  if (!text) return;
                  setMessage("");
                  const error = await api.send(booking.id, text);
                  if (error) {
                    toast.error(error);
                    setMessage(text);
                  }
                }}
              />
            </div>
          </Card>
        </div>

        <div className="grid gap-4">
          <Card className="grid gap-2">
            <CardTitle className="mb-1">{bd.actions}</CardTitle>
            {["confirmed", "active", "completed"].includes(booking.status) && (
              <div className="rounded bg-surface px-3 py-2.5 text-[13px]">
                {unit ? bd.assigned(unit.code, unit.plate) : bd.deferred}
              </div>
            )}
            {canDecide && (
              <>
                {booking.status === "documents_required" && (
                  <div className="rounded bg-tone-amber-bg px-3 py-2.5 text-[13px] text-tone-amber-fg">
                    {bd.waiting}
                  </div>
                )}
                <ActionButton variant="primary" size="lg" onClick={() => setModal({ kind: "approve" })}>
                  {bd.accept}
                </ActionButton>
                <ActionButton size="lg" onClick={() => setModal({ kind: "info" })}>
                  {bd.requestInfo}
                </ActionButton>
                <ActionButton variant="outlineRed" size="lg" onClick={() => setModal({ kind: "reject" })}>
                  {bd.decline}
                </ActionButton>
                <Hint className="mt-1">{bd.neverAuto}</Hint>
              </>
            )}
            {booking.status === "confirmed" && (
              <ActionButton className="h-10 text-sm" onClick={() => setModal({ kind: "assign" })}>
                {unit ? bd.changeVehicle : bd.assignVehicle}
              </ActionButton>
            )}
            {booking.status === "confirmed" && unit && (
              <ActionButton variant="primary" size="lg" disabled={busy} onClick={() => void act("start", bd.rentalStarted)}>
                {bd.handover}
              </ActionButton>
            )}
            {booking.status === "active" && (
              <ActionButton variant="dark" size="lg" disabled={busy} onClick={() => void act("complete", bd.rentalClosed)}>
                {bd.closeRental}
              </ActionButton>
            )}
            {isFinal && <p className="m-0 text-[13px] text-muted">{bd.noActions}</p>}
          </Card>

          <Card>
            <CardTitle className="mb-3.5">{bd.history}</CardTitle>
            {[...booking.history].reverse().map((h, i) => (
              <div key={i} className="flex gap-3 pb-3">
                <span className={cn("mt-1 h-2.5 w-2.5 shrink-0 rounded-full", DOT[h.status])} />
                <div className="text-sm">
                  <b className="font-medium">{t.company.status.booking[h.status]}</b>
                  <div className="text-xs text-faint">
                    {shortDateTime(h.at)} · {h.by === "Driver" ? bd.byDriver : h.by}
                  </div>
                </div>
              </div>
            ))}
          </Card>
        </div>
      </div>

      {(modal?.kind === "approve" || modal?.kind === "assign") && (
        <VehicleModal booking={booking} mode={modal.kind} onClose={() => setModal(null)} />
      )}
      {(modal?.kind === "info" || modal?.kind === "reject") && (
        <MessageModal bookingId={booking.id} kind={modal.kind} onClose={() => setModal(null)} />
      )}
      {modal?.kind === "doc" && (
        <Modal
          title={`${t.company.docTypes[modal.doc.type]} — ${driverName(driver)}`}
          cancelLabel={t.common.close}
          onClose={() => setModal(null)}
        >
          <div className="mb-2 flex aspect-3/2 items-center justify-center rounded-md bg-[repeating-linear-gradient(45deg,#F4F4F4_0_10px,#EDEDED_10px_20px)] font-mono text-xs text-muted">
            {bd.docPreview(modal.doc.file ?? "")}
          </div>
        </Modal>
      )}
    </>
  );
}

/** Accept (with optional deferred assignment) or assign a physical vehicle. */
function VehicleModal({
  booking,
  mode,
  onClose,
}: {
  booking: Booking;
  mode: "approve" | "assign";
  onClose: () => void;
}) {
  const t = useT();
  const vm = t.company.vehicleModal;
  const { state, toast } = useCompany();
  const api = useBookingApi();
  const [busy, setBusy] = useState(false);
  const units = unitsOf(state, booking.listingId);
  const firstFree = units.find((u) => unitFree(state, u, booking.start, booking.end, booking.id));
  // undefined = nothing chosen, null = deferred assignment.
  const [pick, setPick] = useState<string | null | undefined>(
    mode === "assign" ? (booking.unitId ?? firstFree?.id) : (firstFree?.id ?? null),
  );
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const noneFree = !firstFree;

  return (
    <Modal
      title={mode === "approve" ? vm.acceptTitle(booking.id) : vm.assignTitle}
      text={noneFree ? vm.noneFree : vm.choose(longDate(booking.start), longDate(booking.end))}
      okLabel={mode === "approve" ? vm.acceptOk : vm.assignOk}
      error={error}
      onClose={onClose}
      onOk={async () => {
        if (busy) return;
        if (pick === undefined || (mode === "assign" && !pick)) {
          return setError(mode === "approve" ? vm.chooseOrDefer : vm.chooseAvailable);
        }
        setBusy(true);
        const err =
          mode === "approve"
            ? await api.act(booking.id, "accept", { vehicleId: pick ?? undefined, message: text.trim() || undefined })
            : await api.act(booking.id, "assign", { vehicleId: pick });
        setBusy(false);
        if (err) return setError(err);
        toast.success(mode === "approve" ? vm.accepted : vm.assigned);
        onClose();
      }}
    >
      <div className="mb-3.5 grid gap-1.5">
        {units.map((u) => {
          const conflict = unitConflicts(state, u.id, booking.start, booking.end, booking.id)[0];
          const disabled = u.status !== "active" || !!conflict;
          const note =
            u.status !== "active"
              ? vm.inMaintenance
              : conflict
                ? conflict.type === "booking"
                  ? vm.alreadyBooked(shortDate(conflict.start), shortDate(conflict.end), conflict.ref)
                  : vm.blocked(conflict.ref)
                : vm.available(u.plate);
          return (
            <ChoiceRow key={u.id} name="unit" checked={pick === u.id} disabled={disabled} onChange={() => setPick(u.id)}>
              <span className="min-w-19 font-mono text-[13px] font-medium">{u.code}</span>
              <span className={cn("flex-1 text-[13px]", disabled ? "text-tone-red-fg" : "text-tone-green-fg")}>
                {note}
              </span>
            </ChoiceRow>
          );
        })}
        {mode === "approve" && (
          <ChoiceRow name="unit" checked={pick === null} onChange={() => setPick(null)}>
            <span className="text-[13px]">{vm.later}</span>
          </ChoiceRow>
        )}
      </div>
      {mode === "approve" && (
        <textarea
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={vm.messagePlaceholder}
          className={cn(textArea, "border-transparent")}
        />
      )}
    </Modal>
  );
}

/** Decline, or ask the driver for documents — both need a message. */
function MessageModal({
  bookingId,
  kind,
  onClose,
}: {
  bookingId: string;
  kind: "info" | "reject";
  onClose: () => void;
}) {
  const t = useT();
  const mm = t.company.messageModal;
  const { toast } = useCompany();
  const api = useBookingApi();
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const reject = kind === "reject";

  return (
    <Modal
      title={reject ? mm.declineTitle : mm.infoTitle}
      text={reject ? mm.declineText : mm.infoText}
      okLabel={reject ? mm.declineOk : mm.infoOk}
      okVariant={reject ? "danger" : "primary"}
      error={error}
      onClose={onClose}
      onOk={async () => {
        if (busy) return;
        if (!text.trim()) return setError(mm.required);
        setBusy(true);
        const err = reject
          ? await api.act(bookingId, "decline", { reason: text.trim() })
          : await api.act(bookingId, "request-documents", { note: text.trim() });
        setBusy(false);
        if (err) return setError(err);
        toast.success(reject ? mm.declined : mm.infoSent);
        onClose();
      }}
    >
      <textarea
        rows={3}
        autoFocus
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setError("");
        }}
        placeholder={reject ? mm.declinePlaceholder : mm.infoPlaceholder}
        className={cn(textArea, error ? "border-tone-red-fg" : "border-transparent")}
      />
    </Modal>
  );
}
